import QRCode from "qrcode";
import {
  ArrowLeft,
  ArrowRight,
  Copy,
  Dices,
  ListPlus,
  LockKeyhole,
  LogIn,
  MessageCircleHeart,
  Pencil,
  Plus,
  QrCode,
  RefreshCw,
  RotateCcw,
  Send,
  Share2,
  Sparkles,
  Trash2,
  Trophy,
  X,
  createIcons,
} from "lucide";

const icons = {
  ArrowLeft,
  ArrowRight,
  Copy,
  Dices,
  ListPlus,
  LockKeyhole,
  LogIn,
  MessageCircleHeart,
  Pencil,
  Plus,
  QrCode,
  RefreshCw,
  RotateCcw,
  Send,
  Share2,
  Sparkles,
  Trash2,
  Trophy,
  X,
};

const LEVEL_NAMES = {
  1: "轻松破冰",
  2: "深入了解",
  3: "婚前核对",
};
const RECONNECT_DELAYS = [800, 1_500, 3_000, 5_000, 8_000];
const QUESTION_DRAFTS_KEY = "closer-dice:question-drafts";
const FALLBACK_TAGS_BY_LEVEL = {
  1: ["日常", "兴趣", "成长", "性格", "沟通", "情感", "家庭", "事业", "金钱", "未来", "亲密", "其他"],
  2: ["成长", "性格", "日常", "沟通", "情感", "家庭", "事业", "金钱", "兴趣", "未来", "亲密", "婚育", "其他"],
  3: ["未来", "情感", "性格", "沟通", "日常", "家庭", "金钱", "婚育", "事业", "成长", "兴趣", "亲密", "其他"],
};
const FATAL_ERRORS = new Set([
  "INVALID_CREDENTIALS",
  "ROOM_EXPIRED",
  "ROOM_NOT_FOUND",
  "AUTHENTICATION_REQUIRED",
]);

const screens = [...document.querySelectorAll("[data-screen]")];
const ui = Object.fromEntries(
  [
    "connectionStatus",
    "roomBadge",
    "addQuestionButton",
    "accessForm",
    "accessPassword",
    "accessError",
    "accessSubmit",
    "createTab",
    "joinTab",
    "playerName",
    "createPanel",
    "joinPanel",
    "createRoom",
    "roomCodeInput",
    "joinRoom",
    "roomCodeText",
    "copyCode",
    "shareRoom",
    "roomQr",
    "waitingPlayers",
    "waitingStatus",
    "levelOptions",
    "levelStatus",
    "roundNumber",
    "selfName",
    "selfDie",
    "selfRoll",
    "opponentName",
    "opponentDie",
    "opponentRollStatus",
    "opponentPlaceholder",
    "diceStatus",
    "roundOutcome",
    "questionGrid",
    "refreshQuestions",
    "questionRoleStatus",
    "selectedTag",
    "selectedQuestion",
    "continueGame",
    "answeringStatus",
    "leaveQuestionEditor",
    "questionDraftForm",
    "newQuestionText",
    "newQuestionLevel",
    "newQuestionTag",
    "draftFormError",
    "saveQuestionDraft",
    "cancelDraftEdit",
    "draftCount",
    "emptyDrafts",
    "questionDraftList",
    "submitQuestions",
    "exitQuestionEditor",
    "questionUnlockDialog",
    "questionUnlockForm",
    "questionPassword",
    "questionPasswordError",
    "questionUnlockSubmit",
    "closeQuestionUnlock",
    "messageTitle",
    "messageText",
    "messageAction",
    "toast",
  ].map((id) => [id, document.getElementById(id)]),
);

const app = {
  session: null,
  socket: null,
  snapshot: null,
  previousPhase: null,
  reconnectAttempt: 0,
  reconnectTimer: null,
  fatal: false,
  connected: false,
  localRolling: false,
  rollAnimationTimer: null,
  revealingOutcome: false,
  transitionTimer: null,
  delayedQuestionKey: "",
  messageAction: null,
  accessAuthorized: false,
  questionToken: null,
  questionTags: structuredClone(FALLBACK_TAGS_BY_LEVEL),
  drafts: loadQuestionDrafts(),
  editingDraftId: null,
  activeScreen: "access",
};

document.addEventListener("DOMContentLoaded", init);

async function init() {
  createIcons({ icons, attrs: { "aria-hidden": "true" } });
  bindEvents();
  ui.playerName.value = localStorage.getItem("closer-dice:name") || "";
  rememberInviteFromUrl();
  showScreen("access");
  setConnection("connecting", "正在验证");
  await checkAccess();
}

function bindEvents() {
  ui.accessForm.addEventListener("submit", submitAccessPassword);
  ui.addQuestionButton.addEventListener("click", openQuestionUnlock);
  ui.questionUnlockForm.addEventListener("submit", unlockQuestionEditor);
  ui.closeQuestionUnlock.addEventListener("click", closeQuestionUnlock);
  ui.questionUnlockDialog.addEventListener("close", resetQuestionUnlock);
  ui.questionDraftForm.addEventListener("submit", saveDraftFromForm);
  ui.newQuestionLevel.addEventListener("change", renderTagOptions);
  ui.cancelDraftEdit.addEventListener("click", resetDraftForm);
  ui.questionDraftList.addEventListener("click", handleDraftListClick);
  ui.submitQuestions.addEventListener("click", submitQuestionDrafts);
  ui.leaveQuestionEditor.addEventListener("click", exitQuestionEditor);
  ui.exitQuestionEditor.addEventListener("click", exitQuestionEditor);
  ui.createTab.addEventListener("click", () => activateEntryTab("create"));
  ui.joinTab.addEventListener("click", () => activateEntryTab("join"));
  ui.createRoom.addEventListener("click", createRoom);
  ui.joinRoom.addEventListener("click", joinRoom);
  ui.playerName.addEventListener("keydown", (event) => {
    if (event.key === "Enter") (ui.joinPanel.hidden ? createRoom : joinRoom)();
  });
  ui.roomCodeInput.addEventListener("input", () => {
    ui.roomCodeInput.value = normalizeRoomCode(ui.roomCodeInput.value);
  });
  ui.roomCodeInput.addEventListener("keydown", (event) => {
    if (event.key === "Enter") joinRoom();
  });
  ui.copyCode.addEventListener("click", copyRoomCode);
  ui.shareRoom.addEventListener("click", shareRoom);
  ui.levelOptions.addEventListener("click", (event) => {
    const option = event.target.closest("[data-level]");
    if (!option || option.disabled) return;
    sendAction("select_level", { level: Number(option.dataset.level) });
  });
  ui.selfRoll.addEventListener("click", rollDice);
  ui.refreshQuestions.addEventListener("click", () => sendAction("refresh_questions"));
  ui.questionGrid.addEventListener("click", (event) => {
    const card = event.target.closest("[data-question-id]");
    if (!card || card.disabled) return;
    sendAction("select_question", { questionId: card.dataset.questionId });
  });
  ui.continueGame.addEventListener("click", () => sendAction("continue"));
  ui.messageAction.addEventListener("click", () => app.messageAction?.());
  document.querySelector(".brand").addEventListener("click", (event) => {
    event.preventDefault();
    if (!app.accessAuthorized) return;
    if (app.session) showToast("当前房间仍在进行中");
    else if (app.activeScreen === "add-question") exitQuestionEditor();
    else showScreen("home");
  });
  window.addEventListener("online", () => {
    if (app.session && !app.connected) connectRealtime();
    else if (!app.session) setConnection("online", "可进入");
  });
  window.addEventListener("offline", () => setConnection("offline", "网络断开"));
}

async function checkAccess() {
  try {
    const status = await apiGet(accessStatusPath());
    if (status?.authorized) {
      ui.addQuestionButton.hidden = status.scope === "local";
      enterAuthorizedSite();
      return;
    }
    showAccessGate();
  } catch (error) {
    if (["ACCESS_REQUIRED", "AUTHENTICATION_REQUIRED", "INVALID_ACCESS"].includes(error.code)) {
      showAccessGate();
      return;
    }
    showAccessGate(error.message || "暂时无法验证，请重试");
  }
}

function showAccessGate(message = "") {
  app.accessAuthorized = false;
  showScreen("access");
  setConnection(navigator.onLine === false ? "offline" : "online", "需要密码");
  setFormError(ui.accessError, message);
  window.setTimeout(() => ui.accessPassword.focus(), 0);
}

async function submitAccessPassword(event) {
  event.preventDefault();
  const password = ui.accessPassword.value;
  if (!password) {
    setFormError(ui.accessError, "请输入访问密码");
    ui.accessPassword.focus();
    return;
  }
  setFormError(ui.accessError, "");
  setButtonBusy(ui.accessSubmit, true, "正在验证…");
  try {
    const result = await api("/api/access", { password, ...accessContext() });
    if (!result?.authorized) throw new ClientError("INVALID_ACCESS", "密码错误");
    ui.accessPassword.value = "";
    enterAuthorizedSite();
  } catch (error) {
    setFormError(
      ui.accessError,
      ["INVALID_ACCESS", "INVALID_PASSWORD", "UNAUTHORIZED"].includes(error.code)
        ? "密码错误"
        : error.message || "暂时无法验证",
    );
    ui.accessPassword.select();
  } finally {
    setButtonBusy(ui.accessSubmit, false);
  }
}

function enterAuthorizedSite() {
  app.accessAuthorized = true;
  setConnection("online", "可进入");
  const roomCode = roomCodeFromUrl();
  if (roomCode) {
    ui.roomCodeInput.value = roomCode;
    const saved = loadSession(roomCode);
    if (saved) {
      app.session = saved;
      ui.playerName.value = saved.name;
      showScreen("waiting");
      renderRoomIdentity(roomCode);
      connectRealtime();
      return;
    }
    activateEntryTab("join");
  }
  showScreen("home");
}

function openQuestionUnlock() {
  app.questionToken = null;
  resetQuestionUnlock();
  if (typeof ui.questionUnlockDialog.showModal === "function") {
    ui.questionUnlockDialog.showModal();
  } else {
    ui.questionUnlockDialog.setAttribute("open", "");
  }
  window.setTimeout(() => ui.questionPassword.focus(), 0);
}

function closeQuestionUnlock() {
  if (typeof ui.questionUnlockDialog.close === "function") ui.questionUnlockDialog.close();
  else ui.questionUnlockDialog.removeAttribute("open");
}

function resetQuestionUnlock() {
  ui.questionPassword.value = "";
  setFormError(ui.questionPasswordError, "");
}

async function unlockQuestionEditor(event) {
  event.preventDefault();
  const password = ui.questionPassword.value;
  if (!password) {
    setFormError(ui.questionPasswordError, "请输入加题密码");
    ui.questionPassword.focus();
    return;
  }
  setFormError(ui.questionPasswordError, "");
  setButtonBusy(ui.questionUnlockSubmit, true, "正在验证…");
  try {
    const result = await api("/api/questions/unlock", { password, ...accessContext() });
    if (!result?.authorized || !result?.token) {
      throw new ClientError("INVALID_PASSWORD", "密码错误");
    }
    app.questionToken = result.token;
    const metadata = await apiGet("/api/questions/meta", { token: app.questionToken });
    app.questionTags = normalizeTagsByLevel(metadata?.tagsByLevel);
    closeQuestionUnlock();
    openQuestionEditor();
  } catch (error) {
    app.questionToken = null;
    setFormError(
      ui.questionPasswordError,
      ["INVALID_PASSWORD", "UNAUTHORIZED", "FORBIDDEN"].includes(error.code)
        ? "密码错误"
        : error.message || "暂时无法进入",
    );
    ui.questionPassword.select();
  } finally {
    setButtonBusy(ui.questionUnlockSubmit, false);
  }
}

function openQuestionEditor() {
  app.drafts = loadQuestionDrafts();
  resetDraftForm();
  renderDraftList();
  showScreen("add-question");
  window.setTimeout(() => ui.newQuestionText.focus(), 0);
}

function exitQuestionEditor() {
  app.questionToken = null;
  resetDraftForm();
  showScreen("home");
}

function saveDraftFromForm(event) {
  event.preventDefault();
  const question = ui.newQuestionText.value.trim();
  const level = Number(ui.newQuestionLevel.value);
  const tag = ui.newQuestionTag.value;
  if (!question) {
    setFormError(ui.draftFormError, "先写下问题内容");
    ui.newQuestionText.focus();
    return;
  }
  if (![1, 2, 3].includes(level) || !tag) {
    setFormError(ui.draftFormError, "请选择级别和标签");
    return;
  }

  const draft = { id: app.editingDraftId || createDraftId(), question, level, tag };
  const existingIndex = app.drafts.findIndex((item) => item.id === draft.id);
  if (existingIndex >= 0) app.drafts.splice(existingIndex, 1, draft);
  else app.drafts.push(draft);
  persistQuestionDrafts();
  renderDraftList();
  resetDraftForm();
  showToast(existingIndex >= 0 ? "修改已保存" : "已加入待提交清单");
  ui.newQuestionText.focus();
}

function resetDraftForm() {
  app.editingDraftId = null;
  ui.questionDraftForm.reset();
  ui.newQuestionLevel.value = "1";
  renderTagOptions();
  setFormError(ui.draftFormError, "");
  ui.saveQuestionDraft.querySelector("span").textContent = "加入清单";
  ui.cancelDraftEdit.hidden = true;
}

function renderTagOptions() {
  const level = Number(ui.newQuestionLevel.value) || 1;
  const previous = ui.newQuestionTag.value;
  const tags = app.questionTags[level] || FALLBACK_TAGS_BY_LEVEL[level];
  ui.newQuestionTag.replaceChildren();
  for (const tag of tags) {
    const option = document.createElement("option");
    option.value = tag;
    option.textContent = tag;
    ui.newQuestionTag.append(option);
  }
  if (tags.includes(previous)) ui.newQuestionTag.value = previous;
}

function renderDraftList() {
  ui.questionDraftList.replaceChildren();
  ui.draftCount.textContent = `${app.drafts.length} 题`;
  ui.emptyDrafts.hidden = app.drafts.length > 0;
  ui.submitQuestions.disabled = app.drafts.length === 0;

  app.drafts.forEach((draft, index) => {
    const item = document.createElement("article");
    item.className = "draft-item";
    item.dataset.draftId = draft.id;

    const meta = document.createElement("p");
    meta.className = "draft-meta";
    meta.textContent = `${String(index + 1).padStart(2, "0")} · ${LEVEL_NAMES[draft.level]} · ${draft.tag}`;
    const question = document.createElement("p");
    question.className = "draft-question";
    question.textContent = draft.question;
    const actions = document.createElement("div");
    actions.className = "draft-actions";
    actions.append(
      createDraftAction("edit", "修改", Pencil),
      createDraftAction("delete", "删除", Trash2, "is-danger"),
    );
    item.append(meta, question, actions);
    ui.questionDraftList.append(item);
  });
  createIcons({ icons, attrs: { "aria-hidden": "true" } });
}

function createDraftAction(action, label, Icon, extraClass = "") {
  const button = document.createElement("button");
  button.className = `draft-action ${extraClass}`.trim();
  button.type = "button";
  button.dataset.action = action;
  const icon = document.createElement("i");
  icon.dataset.lucide = Icon === Pencil ? "pencil" : "trash-2";
  icon.setAttribute("aria-hidden", "true");
  const text = document.createElement("span");
  text.textContent = label;
  button.append(icon, text);
  return button;
}

function handleDraftListClick(event) {
  const action = event.target.closest("[data-action]");
  const item = event.target.closest("[data-draft-id]");
  if (!action || !item) return;
  const index = app.drafts.findIndex((draft) => draft.id === item.dataset.draftId);
  if (index < 0) return;
  if (action.dataset.action === "delete") {
    const [removed] = app.drafts.splice(index, 1);
    if (app.editingDraftId === removed.id) resetDraftForm();
    persistQuestionDrafts();
    renderDraftList();
    showToast("已从清单删除");
    return;
  }
  const draft = app.drafts[index];
  app.editingDraftId = draft.id;
  ui.newQuestionText.value = draft.question;
  ui.newQuestionLevel.value = String(draft.level);
  renderTagOptions();
  ui.newQuestionTag.value = draft.tag;
  ui.saveQuestionDraft.querySelector("span").textContent = "保存修改";
  ui.cancelDraftEdit.hidden = false;
  setFormError(ui.draftFormError, "");
  ui.questionDraftForm.scrollIntoView({ behavior: "smooth", block: "start" });
  ui.newQuestionText.focus({ preventScroll: true });
}

async function submitQuestionDrafts() {
  if (!app.drafts.length) return;
  if (ui.newQuestionText.value.trim()) {
    setFormError(ui.draftFormError, "输入框里还有未加入清单的问题");
    ui.newQuestionText.focus();
    return;
  }
  setButtonBusy(ui.submitQuestions, true, "正在提交…");
  try {
    await api("/api/questions", {
      questions: app.drafts.map(({ question, level, tag }) => ({ question, level, tag })),
    }, { token: app.questionToken });
    app.drafts = [];
    persistQuestionDrafts();
    app.questionToken = null;
    renderDraftList();
    showScreen("home");
    showToast("新问题已加入题库");
  } catch (error) {
    if (["UNAUTHORIZED", "FORBIDDEN", "TOKEN_EXPIRED"].includes(error.code)) {
      app.questionToken = null;
      showToast("加题验证已失效，草稿仍为你保留");
      showScreen("home");
    } else {
      setFormError(ui.draftFormError, error.message || "提交失败，请重试");
    }
  } finally {
    setButtonBusy(ui.submitQuestions, false);
    ui.submitQuestions.disabled = app.drafts.length === 0;
  }
}

function normalizeTagsByLevel(value) {
  const normalized = {};
  for (const level of [1, 2, 3]) {
    const source = Array.isArray(value?.[level]) ? value[level] : FALLBACK_TAGS_BY_LEVEL[level];
    const tags = [...new Set(source.map((tag) => String(tag).trim()).filter(Boolean))];
    if (!tags.includes("其他")) tags.push("其他");
    normalized[level] = tags;
  }
  return normalized;
}

function setFormError(element, message) {
  element.textContent = message;
  element.hidden = !message;
}

function activateEntryTab(tab) {
  const isCreate = tab === "create";
  ui.createTab.classList.toggle("is-active", isCreate);
  ui.createTab.setAttribute("aria-selected", String(isCreate));
  ui.joinTab.classList.toggle("is-active", !isCreate);
  ui.joinTab.setAttribute("aria-selected", String(!isCreate));
  ui.createPanel.hidden = !isCreate;
  ui.joinPanel.hidden = isCreate;
}

async function createRoom() {
  const name = cleanName();
  if (!name) return;
  setButtonBusy(ui.createRoom, true, "正在创建…");
  try {
    const credentials = await api("/api/rooms", { name });
    enterRoom(credentials, name);
  } catch (error) {
    handleUiError(error);
  } finally {
    setButtonBusy(ui.createRoom, false);
  }
}

async function joinRoom() {
  const name = cleanName();
  const roomCode = normalizeRoomCode(ui.roomCodeInput.value);
  if (!name) return;
  if (roomCode.length !== 6) {
    showToast("请输入六位房间码");
    ui.roomCodeInput.focus();
    return;
  }
  setButtonBusy(ui.joinRoom, true, "正在加入…");
  try {
    const invite = invitationToken(roomCode);
    const credentials = await api(`/api/rooms/${encodeURIComponent(roomCode)}/join`, {
      name,
      ...(invite ? { invite } : {}),
    });
    enterRoom(credentials, name);
  } catch (error) {
    if (["ROOM_FULL", "ROOM_NOT_FOUND", "ROOM_EXPIRED"].includes(error.code)) {
      showMessage(
        error.code === "ROOM_FULL" ? "房间已经坐满" : "没有进入这个房间",
        error.message,
        goHome,
        "返回首页",
      );
    } else {
      handleUiError(error);
    }
  } finally {
    setButtonBusy(ui.joinRoom, false);
  }
}

function enterRoom(credentials, name) {
  const invite = credentials.invite || credentials.inviteToken || invitationToken(credentials.roomCode) || "";
  const session = {
    roomCode: credentials.roomCode,
    playerId: credentials.playerId,
    token: credentials.token,
    name,
    ...(invite ? { invite } : {}),
  };
  app.session = session;
  app.fatal = false;
  saveSession(session);
  if (invite) saveInvite(session.roomCode, invite);
  localStorage.setItem("closer-dice:name", name);
  history.replaceState(null, "", invitationPath(session.roomCode, invite));
  renderRoomIdentity(session.roomCode);
  showScreen("waiting");
  connectRealtime();
}

function connectRealtime() {
  if (
    !app.session
    || app.fatal
    || app.socket?.readyState === WebSocket.OPEN
    || app.socket?.readyState === WebSocket.CONNECTING
  ) return;
  window.clearTimeout(app.reconnectTimer);
  setConnection("connecting", app.reconnectAttempt ? "正在重连" : "连接中");
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  const { roomCode, playerId } = app.session;
  const socketUrl = new URL(`${protocol}//${location.host}/ws`);
  socketUrl.searchParams.set("room", roomCode);
  socketUrl.searchParams.set("player", playerId);
  const invite = invitationToken(roomCode);
  if (invite) socketUrl.searchParams.set("invite", invite);
  const socket = new WebSocket(socketUrl);
  app.socket = socket;

  socket.addEventListener("message", (event) => {
    if (app.socket !== socket) return;
    let message;
    try {
      message = JSON.parse(event.data);
    } catch {
      return;
    }

    if (message.type === "authentication_required") {
      socket.send(JSON.stringify({ type: "authenticate", token: app.session.token }));
      return;
    }
    if (message.type === "state") {
      app.connected = true;
      app.reconnectAttempt = 0;
      setConnection("online", "已同步");
      applySnapshot(message.state);
      return;
    }
    if (message.type === "error") handleSocketError(message);
  });

  socket.addEventListener("close", () => {
    if (app.socket !== socket) return;
    app.connected = false;
    document.body.classList.add("is-offline");
    if (!app.fatal) scheduleReconnect();
  });
  socket.addEventListener("error", () => {
    if (app.socket === socket) setConnection("offline", "连接中断");
  });
}

function scheduleReconnect() {
  window.clearTimeout(app.reconnectTimer);
  const delay = RECONNECT_DELAYS[Math.min(app.reconnectAttempt, RECONNECT_DELAYS.length - 1)];
  app.reconnectAttempt += 1;
  setConnection("connecting", "正在重连");
  app.reconnectTimer = window.setTimeout(connectRealtime, delay);
}

function handleSocketError(error) {
  if (FATAL_ERRORS.has(error.code)) {
    app.fatal = true;
    app.connected = false;
    app.socket?.close();
    if (app.session) removeSession(app.session.roomCode);
    showMessage("房间已经结束", error.message || "请重新创建房间", goHome, "返回首页");
    return;
  }
  stopLocalRollAnimation();
  showToast(error.message || "这个操作暂时无法完成");
  render();
}

function applySnapshot(snapshot) {
  if (!snapshot?.roomCode || !snapshot.you) return;
  app.previousPhase = app.snapshot?.phase ?? null;
  app.snapshot = snapshot;
  renderRoomIdentity(snapshot.roomCode);

  if (app.localRolling && snapshot.rolls?.[snapshot.you.id] != null) {
    window.clearTimeout(app.rollAnimationTimer);
    app.rollAnimationTimer = window.setTimeout(() => {
      app.localRolling = false;
      render();
    }, 540);
  }

  if (snapshot.phase === "question_select" && app.previousPhase === "rolling") {
    const outcomeKey = `${snapshot.round}:${snapshot.winner?.id}:${Object.values(snapshot.rolls).join("-")}`;
    if (app.delayedQuestionKey !== outcomeKey) {
      app.delayedQuestionKey = outcomeKey;
      stopLocalRollAnimation();
      app.revealingOutcome = true;
      renderDice(snapshot, true);
      showScreen("dice");
      window.clearTimeout(app.transitionTimer);
      app.transitionTimer = window.setTimeout(() => {
        if (app.snapshot?.phase === "question_select" && app.delayedQuestionKey === outcomeKey) {
          app.revealingOutcome = false;
          render();
        }
      }, 1_150);
      return;
    }
  }
  if (snapshot.phase !== "question_select") app.revealingOutcome = false;
  render();
}

function render() {
  const snapshot = app.snapshot;
  if (!snapshot) return;
  const opponent = snapshot.players.find((player) => player.id !== snapshot.you.id) ?? null;
  const bothOnline = snapshot.players.length === 2 && snapshot.players.every((player) => player.connected);
  document.body.classList.toggle("is-offline", !app.connected);

  switch (snapshot.phase) {
    case "waiting":
      renderWaiting(snapshot);
      showScreen("waiting");
      break;
    case "level":
      renderLevel(snapshot, bothOnline);
      showScreen("level");
      break;
    case "rolling":
      renderDice(snapshot, false, bothOnline);
      showScreen("dice");
      break;
    case "question_select":
      if (app.revealingOutcome) {
        renderDice(snapshot, true, bothOnline);
        showScreen("dice");
      } else {
        renderQuestions(snapshot, bothOnline);
        showScreen("questions");
      }
      break;
    case "answering":
      renderAnswering(snapshot, bothOnline);
      showScreen("answering");
      break;
    default:
      showMessage("状态暂时无法显示", "请重新连接房间", connectRealtime, "重新连接");
  }

  if (opponent && !opponent.connected && snapshot.phase !== "waiting") {
    setConnection("connecting", `${opponent.name}暂时离线`);
  }
}

function renderWaiting(snapshot) {
  ui.roomCodeText.textContent = snapshot.roomCode;
  ui.waitingPlayers.replaceChildren();
  for (const player of snapshot.players) {
    const chip = document.createElement("span");
    chip.className = `player-chip ${player.connected ? "is-ready" : "is-pending"}`;
    chip.textContent = player.id === snapshot.you.id ? `${player.name}（你）` : player.name;
    ui.waitingPlayers.append(chip);
  }
  if (snapshot.players.length < 2) {
    const pending = document.createElement("span");
    pending.className = "player-chip is-pending";
    pending.textContent = "等待中";
    ui.waitingPlayers.append(pending);
  }
  ui.waitingStatus.textContent = "把房间码给坐在对面的那个人";
  renderQr(snapshot.roomCode);
}

function renderLevel(snapshot, bothOnline) {
  const proposal = snapshot.levelProposal;
  for (const option of ui.levelOptions.querySelectorAll("[data-level]")) {
    const selected = Number(option.dataset.level) === proposal?.level;
    option.setAttribute("aria-checked", String(selected));
    option.disabled = !snapshot.can.selectLevel || !app.connected || !bothOnline;
  }
  if (!bothOnline) {
    ui.levelStatus.textContent = "等对方重新连接后再选择";
  } else if (!proposal) {
    ui.levelStatus.textContent = "先选一个级别，对方选择相同后开始";
  } else if (proposal.awaitingYourConfirmation) {
    ui.levelStatus.textContent = `${proposal.player.name}选择了${LEVEL_NAMES[proposal.level]}，点同一级别确认`;
  } else {
    const opponent = snapshot.players.find((player) => player.id !== snapshot.you.id);
    ui.levelStatus.textContent = `已选择${LEVEL_NAMES[proposal.level]}，等${opponent?.name || "对方"}确认`;
  }
}

function renderDice(snapshot, revealOutcome = false, bothOnline = true) {
  const opponent = snapshot.players.find((player) => player.id !== snapshot.you.id) ?? null;
  const selfRoll = snapshot.rolls?.[snapshot.you.id] ?? null;
  const opponentRoll = opponent ? snapshot.rolls?.[opponent.id] ?? null : null;
  const tieRolls = snapshot.lastTie?.rolls ?? null;
  const showingTie = Boolean(snapshot.lastTie && snapshot.phase === "rolling");
  const shownSelfRoll = tieRolls?.[snapshot.you.id] ?? selfRoll;
  const shownOpponentRoll = opponent ? tieRolls?.[opponent.id] ?? opponentRoll : null;

  ui.roundNumber.textContent = `第 ${snapshot.round} 轮`;
  const levelBadge = document.querySelector(".screen-dice .level-badge");
  levelBadge.textContent = LEVEL_NAMES[snapshot.level] || "等待选级";
  ui.selfName.textContent = `${snapshot.you.name}（你）`;
  ui.opponentName.textContent = opponent?.name || "等待对方";

  setDie(ui.selfDie, shownSelfRoll, app.localRolling);
  setDie(ui.opponentDie, shownOpponentRoll, false);
  ui.selfRoll.disabled = !snapshot.can.roll || !app.connected || !bothOnline || app.localRolling;
  ui.selfRoll.querySelector("span").textContent = showingTie
    ? "再摇一次"
    : selfRoll == null
      ? "摇骰子"
      : "已经摇过";

  const selfPanel = ui.selfDie.closest(".player-panel");
  const opponentPanel = ui.opponentDie.closest(".player-panel");
  selfPanel.dataset.playerState = showingTie ? "tie" : selfRoll == null ? "ready" : "rolled";
  opponentPanel.dataset.playerState = showingTie ? "tie" : opponentRoll == null ? "waiting" : "rolled";
  selfPanel.removeAttribute("data-result");
  opponentPanel.removeAttribute("data-result");
  ui.selfName.nextElementSibling.textContent = showingTie
    ? `上次同为 ${shownSelfRoll} 点`
    : selfRoll == null
      ? "准备好了"
      : `已摇出 ${selfRoll} 点`;
  ui.opponentRollStatus.textContent = !opponent?.connected
    ? "暂时离线"
    : showingTie
      ? `上次同为 ${shownOpponentRoll} 点`
      : opponentRoll == null
        ? "等待摇骰"
        : `已摇出 ${opponentRoll} 点`;
  ui.opponentPlaceholder.textContent = !opponent?.connected
    ? "等对方回来"
    : showingTie
      ? "再摇一次"
      : opponentRoll == null
        ? "等对方出手"
        : revealOutcome
          ? "结果已揭晓"
          : "对方已经完成";

  if (snapshot.lastTie && snapshot.phase === "rolling") {
    ui.roundOutcome.hidden = false;
    ui.roundOutcome.dataset.result = "tie";
    setOutcome("平局，再摇一次", "这一轮还没有输赢");
    ui.diceStatus.textContent = "两个人重新摇一次";
  } else if (revealOutcome && snapshot.winner && snapshot.loser) {
    const didWin = snapshot.winner.id === snapshot.you.id;
    selfPanel.dataset.result = didWin ? "winner" : "loser";
    opponentPanel.dataset.result = didWin ? "loser" : "winner";
    ui.roundOutcome.hidden = false;
    ui.roundOutcome.dataset.result = didWin ? "winner" : "loser";
    setOutcome(
      didWin ? "你赢了这一轮" : "这一题由你回答",
      didWin ? "从四个问题里选一个" : `等${snapshot.winner.name}选题`,
    );
    ui.diceStatus.textContent = `${snapshot.winner.name} ${snapshot.rolls[snapshot.winner.id]} 点，${snapshot.loser.name} ${snapshot.rolls[snapshot.loser.id]} 点`;
  } else {
    ui.roundOutcome.hidden = true;
    ui.diceStatus.textContent = !bothOnline
      ? "等对方重新连接"
      : selfRoll != null && opponentRoll == null
        ? "已经摇好，等对方出手"
        : selfRoll == null && opponentRoll != null
          ? "对方已经摇好，轮到你了"
          : "两个人都摇过，才会揭晓结果";
  }
}

function renderQuestions(snapshot, bothOnline) {
  const didWin = snapshot.winner?.id === snapshot.you.id;
  ui.questionGrid.replaceChildren();
  snapshot.candidates.forEach((question, index) => {
    const card = document.createElement("button");
    card.className = "question-card";
    card.type = "button";
    card.dataset.questionId = question.id;
    card.setAttribute("aria-pressed", "false");
    card.disabled = !snapshot.can.selectQuestion || !app.connected || !bothOnline;

    const number = document.createElement("span");
    number.className = "question-index";
    number.textContent = String(index + 1).padStart(2, "0");
    const tag = document.createElement("span");
    tag.className = "question-tag";
    tag.textContent = question.tag;
    const text = document.createElement("strong");
    text.textContent = question.question;
    card.append(number, tag, text);
    ui.questionGrid.append(card);
  });

  ui.refreshQuestions.disabled = !snapshot.can.refreshQuestions || !app.connected || !bothOnline;
  ui.questionRoleStatus.textContent = !bothOnline
    ? "等对方重新连接"
    : didWin
      ? `${snapshot.loser.name}回答，由你选一道题`
      : `这轮由你回答，等${snapshot.winner.name}选题`;
}

function renderAnswering(snapshot, bothOnline) {
  const didWin = snapshot.winner?.id === snapshot.you.id;
  ui.selectedTag.textContent = snapshot.selectedQuestion?.tag || "问题";
  ui.selectedQuestion.textContent = snapshot.selectedQuestion?.question || "等待题目";
  ui.continueGame.hidden = !didWin;
  ui.continueGame.disabled = !snapshot.can.continue || !app.connected || !bothOnline;
  ui.answeringStatus.textContent = !bothOnline
    ? "等对方重新连接后继续"
    : didWin
      ? `${snapshot.loser.name}回答完后，由你继续下一轮`
      : `请面对面回答，答完后等${snapshot.winner.name}继续`;
}

function rollDice() {
  if (!app.snapshot?.can.roll || app.localRolling) return;
  app.localRolling = true;
  ui.selfDie.dataset.value = "rolling";
  ui.selfRoll.disabled = true;
  if (!sendAction("roll")) {
    stopLocalRollAnimation();
    render();
  }
}

function sendAction(type, payload = {}) {
  if (!app.connected || app.socket?.readyState !== WebSocket.OPEN) {
    showToast("正在重新连接，请稍等");
    return false;
  }
  try {
    app.socket.send(JSON.stringify({ type, ...payload, requestId: crypto.randomUUID?.() }));
    return true;
  } catch {
    showToast("发送失败，请稍后再试");
    return false;
  }
}

function stopLocalRollAnimation() {
  window.clearTimeout(app.rollAnimationTimer);
  app.rollAnimationTimer = null;
  app.localRolling = false;
}

function setDie(element, value, rolling) {
  if (rolling) {
    element.dataset.value = "rolling";
    element.classList.remove("is-muted");
    element.setAttribute("aria-label", "骰子正在滚动");
    return;
  }
  const visibleValue = Number.isInteger(value) ? value : 1;
  element.dataset.value = String(visibleValue);
  element.classList.toggle("is-muted", !Number.isInteger(value));
  element.setAttribute("aria-label", Number.isInteger(value) ? `骰子：${value}点` : "等待摇骰子");
}

function setOutcome(title, subtitle) {
  ui.roundOutcome.querySelector("strong").textContent = title;
  ui.roundOutcome.querySelector("span").textContent = subtitle;
}

async function renderQr(roomCode) {
  const url = invitationUrl(roomCode);
  if (!roomCode || ui.roomQr.dataset.invitationUrl === url) return;
  ui.roomQr.dataset.invitationUrl = url;
  ui.roomQr.replaceChildren();
  try {
    const canvas = document.createElement("canvas");
    await QRCode.toCanvas(canvas, url, {
      width: 192,
      margin: 1,
      color: { dark: "#242521", light: "#ffffff" },
      errorCorrectionLevel: "M",
    });
    ui.roomQr.append(canvas);
  } catch {
    const fallback = document.createElement("span");
    fallback.textContent = roomCode;
    ui.roomQr.append(fallback);
  }
}

async function copyRoomCode() {
  const code = app.snapshot?.roomCode || app.session?.roomCode;
  if (!code) return;
  await copyText(code);
  showToast("房间码已复制");
}

async function shareRoom() {
  const code = app.snapshot?.roomCode || app.session?.roomCode;
  if (!code) return;
  const data = {
    title: "靠近一点",
    text: `房间码 ${code}`,
    url: invitationUrl(code),
  };
  if (navigator.share) {
    try {
      await navigator.share(data);
      return;
    } catch (error) {
      if (error.name === "AbortError") return;
    }
  }
  await copyText(data.url);
  showToast("邀请链接已复制");
}

function renderRoomIdentity(roomCode) {
  ui.roomBadge.hidden = false;
  ui.roomBadge.textContent = `房间 ${roomCode}`;
  ui.roomCodeText.textContent = roomCode;
}

function setConnection(status, label) {
  ui.connectionStatus.dataset.state = status === "connecting" ? "connecting" : status;
  ui.connectionStatus.querySelector("span:last-child").textContent = label;
  const shouldLock = Boolean(app.session) && status !== "online"
    || status === "offline" && navigator.onLine === false;
  document.body.classList.toggle("is-offline", shouldLock);
}

function showScreen(name) {
  app.activeScreen = name;
  document.body.dataset.screenName = name;
  for (const screen of screens) {
    const active = screen.dataset.screen === name;
    screen.hidden = !active;
    screen.classList.toggle("is-active", active);
  }
  window.scrollTo({ top: 0, behavior: "auto" });
}

function showMessage(title, text, action, actionLabel) {
  app.messageAction = action;
  ui.messageTitle.textContent = title;
  ui.messageText.textContent = text;
  ui.messageAction.querySelector("span").textContent = actionLabel;
  ui.messageAction.hidden = !action;
  showScreen("message");
}

function goHome() {
  app.fatal = true;
  window.clearTimeout(app.reconnectTimer);
  app.socket?.close();
  app.socket = null;
  app.connected = false;
  stopLocalRollAnimation();
  app.revealingOutcome = false;
  window.clearTimeout(app.transitionTimer);
  app.snapshot = null;
  app.session = null;
  ui.roomBadge.hidden = true;
  history.replaceState(null, "", "/");
  setConnection("online", "可进入");
  showScreen("home");
}

function cleanName() {
  const name = ui.playerName.value.trim().replace(/\s+/gu, " ");
  if (!name) {
    showToast("先写下你的称呼");
    ui.playerName.focus();
    return "";
  }
  return name.slice(0, 12);
}

function normalizeRoomCode(value) {
  return String(value || "").toUpperCase().replace(/[^A-Z2-9]/gu, "").slice(0, 6);
}

function roomCodeFromUrl() {
  return normalizeRoomCode(new URLSearchParams(location.search).get("room"));
}

function inviteFromUrl() {
  const invite = new URLSearchParams(location.search).get("invite")?.trim() || "";
  return invite.slice(0, 512);
}

function rememberInviteFromUrl() {
  const roomCode = roomCodeFromUrl();
  const invite = inviteFromUrl();
  if (roomCode && invite) saveInvite(roomCode, invite);
}

function invitationToken(roomCode) {
  if (!roomCode) return "";
  if (app.session?.roomCode === roomCode && app.session.invite) return app.session.invite;
  if (roomCodeFromUrl() === roomCode && inviteFromUrl()) return inviteFromUrl();
  return sessionStorage.getItem(inviteKey(roomCode)) || "";
}

function invitationUrl(roomCode) {
  return `${location.origin}${invitationPath(roomCode, invitationToken(roomCode))}`;
}

function invitationPath(roomCode, invite = "") {
  const params = new URLSearchParams({ room: roomCode });
  if (invite) params.set("invite", invite);
  return `/?${params.toString()}`;
}

function inviteKey(roomCode) {
  return `closer-dice:invite:${roomCode}`;
}

function saveInvite(roomCode, invite) {
  sessionStorage.setItem(inviteKey(roomCode), invite);
}

function accessContext() {
  const room = roomCodeFromUrl();
  const invite = room ? invitationToken(room) : "";
  return {
    ...(room ? { room } : {}),
    ...(invite ? { invite } : {}),
  };
}

function accessStatusPath() {
  const params = new URLSearchParams(accessContext());
  const query = params.toString();
  return `/api/access/status${query ? `?${query}` : ""}`;
}

function sessionKey(roomCode) {
  return `closer-dice:session:${roomCode}`;
}

function saveSession(session) {
  localStorage.setItem(sessionKey(session.roomCode), JSON.stringify(session));
}

function loadSession(roomCode) {
  try {
    const session = JSON.parse(localStorage.getItem(sessionKey(roomCode)) || "null");
    return session?.roomCode === roomCode && session.playerId && session.token ? session : null;
  } catch {
    return null;
  }
}

function removeSession(roomCode) {
  localStorage.removeItem(sessionKey(roomCode));
}

function loadQuestionDrafts() {
  try {
    const drafts = JSON.parse(localStorage.getItem(QUESTION_DRAFTS_KEY) || "[]");
    if (!Array.isArray(drafts)) return [];
    return drafts.filter((draft) => (
      draft
      && typeof draft.id === "string"
      && typeof draft.question === "string"
      && [1, 2, 3].includes(Number(draft.level))
      && typeof draft.tag === "string"
    )).map((draft) => ({
      id: draft.id,
      question: draft.question,
      level: Number(draft.level),
      tag: draft.tag,
    }));
  } catch {
    return [];
  }
}

function persistQuestionDrafts() {
  try {
    localStorage.setItem(QUESTION_DRAFTS_KEY, JSON.stringify(app.drafts));
  } catch {
    showToast("本地存储空间不足，这批草稿暂时只保留在当前页面");
  }
}

function createDraftId() {
  return crypto.randomUUID?.() || `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function api(path, body, options = {}) {
  return requestJson(path, { method: "POST", body, ...options });
}

async function apiGet(path, options = {}) {
  return requestJson(path, { method: "GET", ...options });
}

async function requestJson(path, { method, body, token } = {}) {
  let response;
  try {
    response = await fetch(path, {
      method: method || "GET",
      credentials: "same-origin",
      headers: {
        ...(body === undefined ? {} : { "content-type": "application/json" }),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ClientError("NETWORK_ERROR", "网络暂时不可用");
  }
  let data;
  try {
    data = await response.json();
  } catch {
    throw new ClientError("INVALID_RESPONSE", "服务返回了无法识别的内容");
  }
  if (!response.ok) {
    throw new ClientError(data?.error?.code || "SERVER_ERROR", data?.error?.message || "服务暂时不可用");
  }
  return data;
}

function setButtonBusy(button, busy, label = "") {
  if (busy) {
    button.dataset.originalHtml = button.innerHTML;
    button.disabled = true;
    button.textContent = label;
  } else {
    if (button.dataset.originalHtml) button.innerHTML = button.dataset.originalHtml;
    button.disabled = false;
    createIcons({ icons, attrs: { "aria-hidden": "true" } });
  }
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return;
  } catch {
    const input = document.createElement("textarea");
    input.value = text;
    input.style.position = "fixed";
    input.style.opacity = "0";
    document.body.append(input);
    input.select();
    document.execCommand("copy");
    input.remove();
  }
}

function handleUiError(error) {
  setConnection(
    navigator.onLine === false ? "offline" : "connecting",
    navigator.onLine === false ? "网络断开" : "请求失败，可重试",
  );
  showToast(error.message || "暂时无法完成");
}

function showToast(message) {
  ui.toast.textContent = message;
  ui.toast.hidden = false;
  window.clearTimeout(showToast.timer);
  showToast.timer = window.setTimeout(() => {
    ui.toast.hidden = true;
  }, 2_500);
}

class ClientError extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
}
